import { useEffect, useState, useMemo } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "../../api";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const OvertimeAvg = () => {
  const navigate = useNavigate();
  const itemId = "overtime-avg";
  const fieldOrder = useMemo(() => ["agreement", "date", "employeeName", "employeeSignature"], []);
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return fieldOrder.reduce((acc, f) => {
      acc[f] = (initial[f] ?? "");
      return acc;
    }, {});
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      const normalized = fieldOrder.reduce((acc, f) => {
        acc[f] = (raw[f] ?? "");
        return acc;
      }, {});
      setFormData(normalized);
    } else {
      // Prefill employee name from profile when no saved draft
      (async () => {
        try {
          const resp = await API.get(`/api/registration/${accountId}/profile`);
          const profile = resp.data || {};
          const first = profile.first_name || localStorage.getItem('signup_first_name') || "";
          const last = profile.last_name || localStorage.getItem('signup_last_name') || "";
          const name = `${first} ${last}`.trim();
          if (name) {
            setFormData((prev) => ({ ...prev, employeeName: name }));
            localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify({ ...formData, employeeName: name }));
          }
        } catch (err) {
          // ignore
        }
      })();
    }
  }, [fieldOrder]);

  // const isDateToday = (val) => {
  //   if (!val) return false;
  //   const input = new Date(val);
  //   const now = new Date();
  //   return (
  //     input.getFullYear() === now.getFullYear() &&
  //     input.getMonth() === now.getMonth() &&
  //     input.getDate() === now.getDate()
  //   );
  // };

  const isDateToday = (val) => {
    if (!val) return false;
    
    // Get current date in User's Local Time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');

    // Create a standardized string "YYYY-MM-DD"
    const todayString = `${year}-${month}-${day}`;

    // Compare the strings directly
    return val === todayString;
  };

  // Centralized validation used by both live status updates and the submit handler
  const validateForm = (data) => {
    const errors = {};
    if (!data.agreement) errors.agreement = "Please choose one option";
    if (!data.date) errors.date = "This field is required";
    else if (!isDateToday(data.date)) errors.date = "Date must be today";
    if (!String(data.employeeName || "").trim()) errors.employeeName = "This field is required";
    if (!data.employeeSignature) errors.employeeSignature = "Signature is required";

    const values = fieldOrder.map((f) => (data[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const dateValid = !errors.date;
    const agreementValid = !errors.agreement;
    const signatureValid = !errors.employeeSignature;

    const status = allEmpty
      ? "incomplete"
      : allFilled && dateValid && agreementValid && signatureValid
      ? "complete"
      : "in-progress";

    return { errors, status };
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr("");
    setFieldErrors({});

    const accountId = localStorage.getItem("accountId");
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    // Compute completeness checks locally (avoid relying on effect-scoped vars)
    const { errors, status: newStatus } = validateForm(formData);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setErr("Please fill the required fields!");
      return;
    }

    const payload = { status: newStatus, formData };
    if (accountId && newStatus === "complete") {
      axios
        .post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, payload)
        .catch((err) => console.error("Failed to save item:", err))
        .finally(() => {
          axios
            .post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} })
            .catch(() => {});
          showSuccess("Form completed successfully!");
          navigate("/register");
        });
    } else {
      if (accountId)
        axios
          .post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} })
          .catch(() => {});
      navigate("/register");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      if (field === "date") {
        if (value && isDateToday(value)) delete next[field];
      } else if (field === "agreement") {
        if (["accept", "decline"].includes(String(value))) delete next[field];
      } else {
        if (String(value || "").trim()) delete next[field];
      }
      return next;
    });
  };

  // Auto-save draft. Never set "complete" here (only on Submit). If already complete, preserve it when user reopens and goes back.
  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const existing = (accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null) || localStorage.getItem(`status_${itemId}`);
    const { status } = validateForm(formData);
    const statusToPersist = status === "complete" ? (existing === "complete" ? "complete" : "in-progress") : status;
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    if (accountId && statusToPersist !== "incomplete") {
      axios
        .post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
          status: statusToPersist,
          formData: {},
        })
        .catch((err) => console.error("Failed to sync status:", err));
    }
  }, [formData]);

  const handleClearAll = () => {
    setFormData(fieldOrder.reduce((acc, f) => { acc[f] = ""; return acc; }, {}));
  };

  const handleBack = () => {
    navigate("/register");
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        {/* Top content card */}
        <div className="form-card">
          <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
            <h2 className="form-title" style={{ textAlign: "center" }}>
              Agreement for Overtime Averaging
            </h2>
          </header>

          <section className="form-card-content">
            {err && <div className="error-text" style={{ color: "#dc2626", marginBottom: 12 }}>{err}</div>}
            {/* OLD PARAGRAPHS (2025 format):
            <p className="form-intro">
              I, the undersigned employee, understand that under the Ontario Employment Standards Act, 2000 ("ESA"), anemployee is entitled to have overtime pay for all hours worked in excess of 44 hours per week.
            </p>
            <p className="form-intro paragraph-space">
              I agree to have my hours of work averaged over the period of 4 weeks for the purposes of determining my overtime pay entitlements. Accordingly, I will only be entitled to overtime pay for all hours worked in excess of 176 hours in a 4-week period. For example, if I work 48 hours in one week and 20 hours during the following week, then I will not be entitled to any overtime pay. I will still, however, be entitled to my regular pay.
            </p>
            <p className="form-intro paragraph-space">
              This Agreement expires on December 29th, 2025.
            </p>
            <p className="form-intro">
              I acknowledge and accept that if I wish to cancel this Agreement prior to the expiry, I must give Talent Employment written consent. Below is a chart for 2025 and each year they will be available for pick up at the front desk.
            </p>
            */}

            {/* NEW PARAGRAPHS (2026 format): */}
            <p className="form-intro">
              I, the undersigned employee, understand that under the Ontario Employment Standards Act, 2000 ("ESA"), an employee is entitled to have overtime pay for all hours worked in excess of 44 hours per week.
            </p>
            <p className="form-intro paragraph-space">
              I agree to have my hours of work averaged over the period of <b>4 weeks</b> for the purposes of determining my overtime pay entitlements. Accordingly, I will only be entitled to overtime pay for all hours worked in excess of 176 hours in a 4-week period. For example, if I work 48 hours in one week and 20 hours during the following week, then I will not be entitled to any overtime pay. I will still, however, be entitled to my regular pay.
            </p>
            <p className="form-intro paragraph-space">
              This Agreement expires on December 27th, 2026.
            </p>
            <p className="form-intro">
              I acknowledge and accept that if I wish to cancel this Agreement prior to the expiry, I must give Talent Employment written consent. Below is a chart for 2026 and each year they will be available for pick up at the front desk.
            </p>
            {/* <p className="form-intro warning">
              *Accessibility Plan and Policies are available upon request through Reception*
            </p> */}
            <table className="custom-table">
                <thead>
                    <tr>
                        <th></th>
                        <th>Work Period</th>
                    </tr>
                </thead>
                {/* OLD TABLE DATA (2025):
                <tbody>
                    <tr>
                        <td>1</td>
                        <td>December 30, 2024 - January 26, 2025</td>
                    </tr>
                    <tr>
                        <td>2</td>
                        <td>January 27, 2025 – February 23, 2025</td>
                    </tr>
                    <tr>
                        <td>3</td>
                        <td>February 24, 2025 – March 23, 2025</td>
                    </tr>
                    <tr>
                        <td>4</td>
                        <td>March 24, 2025 - April 20, 2025</td>
                    </tr>
                    <tr>
                        <td>5</td>
                        <td>April 21, 2024 - May 18, 2025</td>
                    </tr>
                    <tr>
                        <td>6</td>
                        <td>May 19, 2025 - June 15, 2025</td>
                    </tr>
                    <tr>
                        <td>7</td>
                        <td>June 16, 2025 - July 13, 2025</td>
                    </tr>
                    <tr>
                        <td>8</td>
                        <td>July 14, 2025 - August 10, 2025</td>
                    </tr>
                    <tr>
                        <td>9</td>
                        <td>August 11, 2025 - September 7, 2025</td>
                    </tr>
                    <tr>
                        <td>10</td>
                        <td>September 8, 2025 - October 5, 2025</td>
                    </tr>
                    <tr>
                        <td>11</td>
                        <td>October 6, 2025 - November 2, 2025</td>
                    </tr>
                    <tr>
                        <td>12</td>
                        <td>November 3, 2025 – November 30, 2025</td>
                    </tr>
                    <tr>
                        <td>13</td>
                        <td>December 1, 2025 - December 28, 2025</td>
                    </tr>
                </tbody>
                */}
                {/* NEW TABLE DATA (2026): */}
                <tbody>
                    <tr>
                        <td>1</td>
                        <td>December 29, 2025 – January 25, 2026</td>
                    </tr>
                    <tr>
                        <td>2</td>
                        <td>January 26, 2026 – February 22, 2026</td>
                    </tr>
                    <tr>
                        <td>3</td>
                        <td>February 23, 2026 – March 22, 2026</td>
                    </tr>
                    <tr>
                        <td>4</td>
                        <td>March 23, 2026 – April 19, 2026</td>
                    </tr>
                    <tr>
                        <td>5</td>
                        <td>April 20, 2026 – May 17, 2026</td>
                    </tr>
                    <tr>
                        <td>6</td>
                        <td>May 18, 2026 – June 14, 2026</td>
                    </tr>
                    <tr>
                        <td>7</td>
                        <td>June 15, 2026 – July 12, 2026</td>
                    </tr>
                    <tr>
                        <td>8</td>
                        <td>July 13, 2026 – August 9, 2026</td>
                    </tr>
                    <tr>
                        <td>9</td>
                        <td>August 10, 2026 – September 6, 2026</td>
                    </tr>
                    <tr>
                        <td>10</td>
                        <td>September 7, 2026 – October 4, 2026</td>
                    </tr>
                    <tr>
                        <td>11</td>
                        <td>October 5, 2026 – November 1, 2026</td>
                    </tr>
                    <tr>
                        <td>12</td>
                        <td>November 2, 2026 – November 29, 2026</td>
                    </tr>
                    <tr>
                        <td>13</td>
                        <td>November 30, 2026 – December 27, 2026</td>
                    </tr>
                </tbody>
            </table>
            <p style={{ fontSize: "12px" }}>Please Choose*</p>
            <label className="option">
              <input
                type="radio"
                name="agreement"
                value="accept"
                checked={formData.agreement === "accept"}
                onChange={(e) => handleInputChange("agreement", e.target.value)}
              />
              <span>I enter this Agreement freely and with a full understanding of its contents.</span>
            </label>
            <label className="option">
              <input
                type="radio"
                name="agreement"
                value="decline"
                checked={formData.agreement === "decline"}
                onChange={(e) => handleInputChange("agreement", e.target.value)}
              />
              <span>I hereby DECLINE the terms of this Agreement; I do not want to average my hours.</span>
            </label>
            {fieldErrors.agreement && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.agreement}</div>}
          </section>

          <div className="grid-2">
            <div className="form-field">
              <label htmlFor="date">Date*</label>
              <DateInput
                id="date"
                value={formData.date}
                onChange={(v) => handleInputChange("date", v)}
                required
              />
              {fieldErrors.date && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.date}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="employeeName">Employee's Full Name*</label>
              <input
                id="employeeName"
                type="text"
                value={formData.employeeName}
                onChange={(e) =>
                  handleInputChange("employeeName", e.target.value)
                }
                required
              />
              {fieldErrors.employeeName && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeName}</div>}
            </div>
          </div>

          {/* Signature panel in a boxed wrapper */}
          <div className="signature-panel-wrapper">
            {/* <label>Employee's Signature*</label> */}
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
            {fieldErrors.employeeSignature && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeSignature}</div>}
          </div>

          <div className="form-buttons">
            <button type="button" onClick={handleBack}>Back</button>
            <button type="submit" onClick={handleSubmit}>Submit</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OvertimeAvg;
